/**
 * Tool shape builder for the Now Assist Skill Plugin.
 * Converts ToolDefinition[] back into ArrowFunctionShape for Fluent code generation.
 * This is the reverse of extractToolDefinitions -- it converts the internal
 * ToolDefinition representation back into Fluent DSL code.
 */

import { TOOL_TYPES } from './defaults'
import {
    type Source,
    type Shape,
    ObjectShape,
    ArrayShape,
    CallExpressionShape,
    IdentifierShape,
    PropertyAccessShape,
    StringLiteralShape,
    VariableStatementShape,
} from '@servicenow/sdk-build-core'
import { ArrowFunctionShape, NowIdShape } from '@servicenow/sdk-build-plugins'
import type { ToolDefinition } from './tool-types'
import { RawCodeShape, sanitizeVarName, templateValueToShape, topologicalSortTools } from './tool-utils'

/**
 * Map from ToolDefinition.type to the ToolGraphBuilder method name.
 */
const TOOL_TYPE_TO_METHOD: Record<string, string> = {
    [TOOL_TYPES.INLINE_SCRIPT]: 'InlineScript',
    [TOOL_TYPES.SCRIPT]: 'Script',
    [TOOL_TYPES.FLOW_ACTION]: 'FlowAction',
    [TOOL_TYPES.SUBFLOW]: 'Subflow',
    [TOOL_TYPES.SKILL]: 'Skill',
    [TOOL_TYPES.WEB_SEARCH]: 'WebSearch',
    [TOOL_TYPES.DECISION]: 'Decision',
}

/**
 * Build the config ObjectShape for a single tool's second argument.
 * Produces the properties that match the ToolGraphBuilder method signature.
 *
 * Uses a plain object (not Record<string, unknown>) to avoid TypeScript
 * index signature access restrictions.
 */
function buildToolConfigProperties(
    source: Source,
    tool: ToolDefinition,
    paramName: string,
    toolNameToVarName: Map<string, string>,
    inputNameMap?: Map<string, string>,
    decisionNames?: Set<string>
): { [key: string]: unknown } {
    // biome-ignore lint/suspicious/noExplicitAny: building dynamic config objects for shape construction
    const props: any = {}

    // $id — emit as NowIdShape so it renders as Now.ID['...'] in Fluent code
    if (tool.$id) {
        props.$id = new NowIdShape({ source, id: tool.$id })
    }

    // $capabilityId — emit as NowIdShape so it renders as Now.ID['...'] in Fluent code
    // Only Script/FlowAction/Subflow tools have $capabilityId
    if (tool.$capabilityId) {
        props.$capabilityId = new NowIdShape({ source, id: tool.$capabilityId })
    }

    // depends -- array of IdentifierShape references to variable names
    // Skip emitting depends for Decision node dependencies, since Decision targets
    // get their dependency implicitly via the Decision's targets array
    if (tool.depends && tool.depends.length > 0) {
        // Filter out Decision dependencies
        const nonDecisionDeps = tool.depends.filter((dep) => !decisionNames?.has(dep))
        if (nonDecisionDeps.length > 0) {
            const depShapes = nonDecisionDeps.map((dep) => {
                const varName = toolNameToVarName.get(dep)
                if (varName) {
                    return new IdentifierShape({ source, name: varName })
                }
                return new StringLiteralShape({ source, literalText: dep })
            })
            props.depends = new ArrayShape({ source, elements: depShapes })
        }
    }

    switch (tool.type) {
        case TOOL_TYPES.INLINE_SCRIPT: {
            if (tool.script !== undefined) {
                props.script = tool.script
            }
            if (tool.truncate) {
                props.truncate = true
            }
            break
        }

        case TOOL_TYPES.SCRIPT: {
            // scriptId is always a raw sys_id string (resolved from ScriptInclude or DbRecord during build)
            if (tool.scriptId) {
                props.scriptId = tool.scriptId
            }
            if (tool.scriptFunctionName) {
                props.scriptFunctionName = tool.scriptFunctionName
            }
            if (tool.inputs && tool.inputs.length > 0) {
                props.inputs = buildInputShapes(source, tool.inputs, paramName, toolNameToVarName, inputNameMap)
            }
            if (tool.output?.$id) {
                const outputProps: Record<string, Shape | string | boolean> = {
                    $id: new NowIdShape({ source, id: tool.output.$id }),
                }
                if (tool.output.name) {
                    outputProps.name = tool.output.name
                }
                if (tool.output.type) {
                    outputProps.type = tool.output.type
                }
                props.output = new ObjectShape({ source, properties: outputProps })
            }
            if (tool.truncate) {
                props.truncate = true
            }
            break
        }

        case TOOL_TYPES.FLOW_ACTION: {
            if (tool.actionId) {
                props.actionId = tool.actionId
            }
            if (tool.inputs && tool.inputs.length > 0) {
                props.inputs = buildFlowActionInputShapes(
                    source,
                    tool.inputs,
                    paramName,
                    toolNameToVarName,
                    inputNameMap
                )
            }
            if (tool.outputs && tool.outputs.length > 0) {
                props.outputs = buildOutputShapes(source, tool.outputs)
            }
            break
        }

        case TOOL_TYPES.SUBFLOW: {
            // Handle subflowId - prefer Now.ID reference if available, fallback to raw sys_id
            if (tool.subflowIdIsReference && tool.subflowIdExplicitId) {
                // Render as Now.ID['subflow_name'] for same-scope references
                props.subflowId = new RawCodeShape({
                    source,
                    code: `Now.ID['${tool.subflowIdExplicitId}']`,
                })
            } else if (tool.subflowId) {
                // Fallback to raw sys_id string
                props.subflowId = tool.subflowId
            }
            if (tool.inputs && tool.inputs.length > 0) {
                props.inputs = buildInputShapes(source, tool.inputs, paramName, toolNameToVarName, inputNameMap)
            }
            // Handle dynamic outputs (like FlowAction)
            if (tool.outputs && tool.outputs.length > 0) {
                props.outputs = buildOutputShapes(source, tool.outputs)
            }
            // Backward compatibility: truncate flag for single default output
            if (tool.truncate) {
                props.truncate = true
            }
            break
        }

        case TOOL_TYPES.SKILL: {
            // Handle skillId - prefer Now.ID reference if available, fallback to raw sys_id
            if (tool.skillIdIsReference && tool.skillIdExplicitId) {
                // Render as Now.ID['skill_name'] for same-scope references
                props.skillId = new RawCodeShape({
                    source,
                    code: `Now.ID['${tool.skillIdExplicitId}']`,
                })
            } else if (tool.skillId) {
                // Fallback to raw sys_id string
                props.skillId = tool.skillId
            }
            if (tool.inputs && tool.inputs.length > 0) {
                // Skill inputs use definitionAttributeId instead of name
                props.inputs = buildSkillInputShapes(source, tool.inputs, paramName, toolNameToVarName, inputNameMap)
            }
            if (tool.skillOutputs) {
                // Build unified outputs object: { key: { definitionAttributeId, truncate? } }
                const outputProps: Record<string, Shape> = {}
                for (const [key, output] of Object.entries(tool.skillOutputs)) {
                    const entryProps: Record<string, unknown> = {
                        definitionAttributeId:
                            typeof output.definitionAttributeId === 'string'
                                ? output.definitionAttributeId
                                : output.definitionAttributeId,
                    }
                    if (output['truncate']) {
                        entryProps['truncate'] = true
                    }
                    outputProps[key] = new ObjectShape({ source, properties: entryProps })
                }
                props.outputs = new ObjectShape({ source, properties: outputProps })
            }
            break
        }

        case TOOL_TYPES.WEB_SEARCH: {
            if (tool.searchType) {
                props.searchType = tool.searchType
            }
            if (tool.queryValueType === 'script' && tool.queryScriptValue) {
                // Emit { type: 'script', script: '...' } ObjectShape for scripted query
                props.query = new ObjectShape({
                    source,
                    properties: { type: 'script', script: tool.queryScriptValue },
                })
            } else if (tool.query) {
                props.query = templateValueToShape(source, tool.query, paramName, toolNameToVarName, inputNameMap)
            }
            if (tool.sitesValueType === 'script' && tool.sitesScriptValue) {
                // Emit { type: 'script', script: '...' } ObjectShape for scripted sites
                props.sites = new ObjectShape({
                    source,
                    properties: { type: 'script', script: tool.sitesScriptValue },
                })
            } else if (tool.sites) {
                props.sites = templateValueToShape(source, tool.sites, paramName, toolNameToVarName, inputNameMap)
            }
            if (tool.aiSearchProviders) {
                props.aiSearchProviders = tool.aiSearchProviders
            }
            if (tool.numResults !== undefined) {
                props.numResults = tool.numResults
            }
            if (tool.country) {
                props.country = tool.country
            }
            if (tool.maxTokens !== undefined) {
                props.maxTokens = tool.maxTokens
            }
            if (tool.truncateConfig) {
                props.truncate = tool.truncateConfig
            }
            break
        }

        case TOOL_TYPES.DECISION: {
            const targetsParam = 'targets'

            if (tool.targets && tool.targets.length > 0) {
                // Add 'as const' to targets array for type-safe Decision config.
                // Without 'as const', TypeScript infers string[] which loses the specific target names
                // and makes the targets parameter an index signature, breaking dot notation.
                const targetsCode = `[${tool.targets.map((t) => `'${t}'`).join(', ')}] as const`
                props.targets = new RawCodeShape({ source, code: targetsCode })
            }

            // branches is always required by the Decision API type
            if (tool.branches && tool.branches.length > 0) {
                // DecisionBranch = { name, to, condition: DecisionCondition }
                const branchItems = tool.branches.map((b, idx) => {
                    const condParts: string[] = []
                    // Use actual branch name if available, otherwise generate one
                    const branchName = b.name || `${b.target}Branch${idx + 1}`
                    condParts.push(`name: '${branchName}'`)
                    condParts.push(`to: ${targetsParam}.${b.target}`)

                    // Try to parse condition string into DecisionCondition object
                    // condition is REQUIRED by DecisionBranch interface
                    if (b.condition) {
                        // Use conditionType to determine rendering:
                        // - 'script': render as { script: '...' }
                        // - 'filter': try to parse into { field, operator, value }
                        if (b.conditionType === 'script') {
                            // Script condition - render directly
                            // Escape both single quotes and newlines for valid JS string
                            const escapedScript = b.condition
                                .replace(/\\/g, '\\\\')
                                .replace(/'/g, "\\'")
                                .replace(/\n/g, '\\n')
                                .replace(/\r/g, '\\r')
                            condParts.push(`condition: { script: '${escapedScript}' }`)
                        } else {
                            // Filter condition (applicability_type = 'condition_expression')
                            // Parse field=value or field!=value
                            const opMap: Record<string, string> = { '=': 'is', '!=': 'is not' }

                            // Match: field then = or !=, then value (don't strip {{}})
                            const filterMatch = b.condition.match(/^(.+?)(!?=)(.*)$/)

                            if (filterMatch?.[1] && filterMatch[2]) {
                                const rawField = filterMatch[1]
                                const operator = opMap[filterMatch[2]] ?? filterMatch[2]
                                const value = filterMatch[3] ?? ''

                                // Check if field is wrapped in {{}} - extract inner part
                                const templateMatch = rawField.match(/^\{\{(.+?)\}\}$/)
                                const innerField = templateMatch ? templateMatch[1] : rawField

                                // Check if inner field is a known input or tool reference
                                const dotIdx = innerField.indexOf('.')
                                const prefix = dotIdx > 0 ? innerField.substring(0, dotIdx) : innerField
                                const isKnownTool = toolNameToVarName.has(prefix)
                                const isKnownInput = inputNameMap?.has(innerField) || inputNameMap?.has(prefix)
                                const isInputPrefix = prefix === 'input'

                                if (templateMatch && (isKnownTool || isKnownInput || isInputPrefix)) {
                                    // Known input/tool wrapped in {{}} - use templateValueToShape
                                    const fieldShape = templateValueToShape(
                                        source,
                                        `{{${innerField}}}`,
                                        paramName,
                                        toolNameToVarName,
                                        inputNameMap
                                    )
                                    condParts.push(
                                        `condition: { field: ${fieldShape.getCode()}, operator: '${operator}', value: '${value}' }`
                                    )
                                } else {
                                    // Not a template reference or unknown - use as simple string literal
                                    const escapedField = rawField.replace(/'/g, "\\'")
                                    condParts.push(
                                        `condition: { field: '${escapedField}', operator: '${operator}', value: '${value}' }`
                                    )
                                }
                            } else {
                                // No = or != found - use entire condition as expression
                                const escapedValue = b.condition.replace(/'/g, "\\'").replace(/\n/g, '\\n')
                                condParts.push(`condition: { expression: '${escapedValue}' }`)
                            }
                        }
                    } else {
                        // No condition provided - use a default true condition
                        condParts.push(`condition: { script: 'return true;' }`)
                    }
                    return `{ ${condParts.join(', ')} }`
                })
                const branchItemShapes = branchItems.map((item) => new RawCodeShape({ source, code: item }))
                props.branches = new ArrowFunctionShape({
                    source,
                    parameters: [targetsParam],
                    statements: [],
                    returnValue: new ArrayShape({ source, elements: branchItemShapes }),
                    implicitReturn: true,
                } as any)
            } else {
                // Empty branches -- still required by API type.
                // Use () => [] (no parameter) to avoid unused-variable detection.
                props.branches = new ArrowFunctionShape({
                    source,
                    parameters: [],
                    statements: [],
                    returnValue: new ArrayShape({ source, elements: [] }),
                    implicitReturn: true,
                } as any)
            }

            if (tool.default) {
                // default is a function: (targets) => targets.TargetName
                // '_end' is used when default points to prompt (parent skill) - it's in targets array
                props.default = new ArrowFunctionShape({
                    source,
                    parameters: [targetsParam],
                    statements: [],
                    returnValue: new PropertyAccessShape({ source, elements: [targetsParam, tool.default] }),
                    implicitReturn: true,
                } as any)
            } else if (tool.targets && tool.targets.length > 0) {
                // Fallback: use the first target as default
                props.default = new ArrowFunctionShape({
                    source,
                    parameters: [targetsParam],
                    statements: [],
                    returnValue: new PropertyAccessShape({ source, elements: [targetsParam, tool.targets[0]] }),
                    implicitReturn: true,
                } as any)
            }
            break
        }
    }

    // Condition
    if (tool.condition) {
        props.condition = {
            ...(tool.condition.type ? { type: tool.condition.type } : {}),
            ...(tool.condition.script ? { script: tool.condition.script } : {}),
        }
    }

    return props
}

/**
 * Build input shapes for tool configurations.
 * Inputs have { name, value } and optionally { type } for FlowAction tools.
 * Value may be a template expression or a ScriptTypeToolInput.
 *
 * @param includeType - When true, includes the 'type' field on each input (used by FlowAction)
 */
function buildInputShapes(
    source: Source,
    inputs: Array<{
        $id?: string
        name: string
        type?: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }>,
    paramName: string,
    toolNameToVarName: Map<string, string>,
    inputNameMap?: Map<string, string>
): ObjectShape[] {
    return inputs.map((inp) => {
        // biome-ignore lint/suspicious/noExplicitAny: building dynamic shape properties
        const inputProps: any = {}
        if (inp.$id) {
            inputProps.$id = new NowIdShape({ source, id: inp.$id })
        }
        inputProps.name = inp.name
        // Include type if present (for Subflow tools)
        if (inp.type) {
            inputProps.type = inp.type
        }
        if (inp.valueType === 'script' && inp.scriptValue) {
            // Emit { type: 'script', script: '...' } ObjectShape for scripted inputs
            inputProps.value = new ObjectShape({
                source,
                properties: { type: 'script', script: inp.scriptValue },
            })
        } else if (inp.value) {
            inputProps.value = templateValueToShape(
                source,
                inp.value,
                paramName,
                toolNameToVarName,
                inputNameMap
            ) as Shape
        }
        return new ObjectShape({ source, properties: inputProps })
    })
}

/**
 * Build input shapes for Skill tools.
 * Skill inputs use `definitionAttributeId` (sys_id of definition attribute) instead of `name`.
 * The ToolDefinition stores the definitionAttributeId in the `name` field internally.
 */
function buildSkillInputShapes(
    source: Source,
    inputs: Array<{
        name: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }>,
    paramName: string,
    toolNameToVarName: Map<string, string>,
    inputNameMap?: Map<string, string>
): ObjectShape[] {
    return inputs.map((inp) => {
        // biome-ignore lint/suspicious/noExplicitAny: building dynamic shape properties
        const inputProps: any = {}
        // Emit as definitionAttributeId (sys_id) instead of name
        inputProps.definitionAttributeId = inp.name
        if (inp.valueType === 'script' && inp.scriptValue) {
            inputProps.value = new ObjectShape({
                source,
                properties: { type: 'script', script: inp.scriptValue },
            })
        } else if (inp.value) {
            inputProps.value = templateValueToShape(
                source,
                inp.value,
                paramName,
                toolNameToVarName,
                inputNameMap
            ) as Shape
        }
        return new ObjectShape({ source, properties: inputProps })
    })
}

/**
 * Build input shapes for FlowAction tools.
 * Inputs have { name, type, value } — value may be a template expression or a ScriptTypeToolInput.
 */
function buildFlowActionInputShapes(
    source: Source,
    inputs: Array<{
        $id?: string
        name: string
        type?: string
        value: string
        scriptValue?: string
        valueType?: 'template' | 'script'
    }>,
    paramName: string,
    toolNameToVarName: Map<string, string>,
    inputNameMap?: Map<string, string>
): ObjectShape[] {
    return inputs.map((inp) => {
        // biome-ignore lint/suspicious/noExplicitAny: building dynamic shape properties
        const inputProps: any = {}
        if (inp.$id) {
            inputProps.$id = new NowIdShape({ source, id: inp.$id })
        }
        inputProps.name = inp.name
        if (inp.type) {
            inputProps.type = inp.type
        }
        if (inp.valueType === 'script' && inp.scriptValue) {
            // Emit { type: 'script', script: '...' } ObjectShape for scripted inputs
            inputProps.value = new ObjectShape({
                source,
                properties: { type: 'script', script: inp.scriptValue },
            })
        } else if (inp.value) {
            inputProps.value = templateValueToShape(
                source,
                inp.value,
                paramName,
                toolNameToVarName,
                inputNameMap
            ) as Shape
        }
        return new ObjectShape({ source, properties: inputProps })
    })
}

/**
 * Build output shapes for FlowAction/Skill tools.
 * Outputs have { name, type, truncate? }.
 */
function buildOutputShapes(
    source: Source,
    outputs: Array<{ $id?: string; name: string; type: string; truncate?: boolean }>
): ObjectShape[] {
    return outputs.map((out) => {
        // biome-ignore lint/suspicious/noExplicitAny: building dynamic shape properties
        const outProps: any = {}
        if (out.$id) {
            outProps.$id = new NowIdShape({ source, id: out.$id })
        }
        outProps.name = out.name
        outProps.type = out.type
        if (out.truncate) {
            outProps.truncate = true
        }
        return new ObjectShape({ source, properties: outProps })
    })
}

/**
 * Build an ArrowFunctionShape that generates the `tools: (t) => { ... }` code
 * from an array of ToolDefinitions.
 *
 * This is the reverse of `extractToolDefinitions` -- it converts the internal
 * ToolDefinition representation back into Fluent DSL code.
 *
 * @param tools - Array of tool definitions extracted from XML records
 * @param source - Source record for shape construction
 * @param inputNameMap - Map of normalized input names to original names
 * @param existingToolVarNames - Map of tool names to existing variable names (for round-trip preservation)
 * @returns ArrowFunctionShape representing the tools arrow function, or undefined if no tools
 */
export function buildToolsArrowFunctionShape(
    tools: ToolDefinition[],
    source: Source,
    inputNameMap?: Map<string, string>,
    existingToolVarNames?: Map<string, string>
): Shape | undefined {
    if (!tools || tools.length === 0) {
        return undefined
    }

    const paramName = 't'

    // Step 1: ALL tools get const assignments so the return statement can reference them.
    // The return statement at the end of the arrow function uses all tool variables,
    // which makes them all "read" and avoids TS6133 "declared but never read" errors.
    const toolNameToVarName = new Map<string, string>()
    for (const tool of tools) {
        const varName = existingToolVarNames?.get(tool.name) ?? sanitizeVarName(tool.name)
        toolNameToVarName.set(tool.name, varName)
    }

    // Step 2: Collect all Decision tool names
    // Tools that depend on Decision nodes should NOT emit explicit 'depends' since
    // the dependency is implicit via the Decision's targets array
    const decisionNames = new Set<string>()
    for (const tool of tools) {
        if (tool.type === TOOL_TYPES.DECISION) {
            decisionNames.add(tool.name)
        }
    }

    // Step 3: Topological sort -- tools that are depended upon must be declared before their dependents
    const sortedTools = topologicalSortTools(tools)

    // Step 4: Build statements for each tool
    const statements: Shape[] = []
    const emittedToolNames = new Set<string>()

    for (const tool of sortedTools) {
        const methodName = TOOL_TYPE_TO_METHOD[tool.type]
        if (!methodName) {
            continue
        }

        // Build config properties
        // Pass decisionNames to skip emitting 'depends' for tools that are Decision targets
        const configProps = buildToolConfigProperties(
            source,
            tool,
            paramName,
            toolNameToVarName,
            inputNameMap,
            decisionNames
        )

        // Create CallExpressionShape: t.MethodName('toolName', { ...config })
        const callExpression = new CallExpressionShape({
            source,
            callee: `${paramName}.${methodName}`,
            args: [
                new StringLiteralShape({ source, literalText: tool.name }),
                new ObjectShape({ source, properties: configProps }),
            ],
        })

        // All tools get const assignments -- the return statement uses them all
        const varName = toolNameToVarName.get(tool.name)!
        statements.push(
            new VariableStatementShape({
                source,
                variableName: varName,
                initializer: callExpression,
            })
        )
        emittedToolNames.add(tool.name)
    }

    // Step 5: Build return value -- an object with all emitted tool handles
    // This enables p.tool.* type inference and makes all const variables "used"
    // ObjectShape.getCode() always uses explicit property assignment (key: value),
    // never shorthand ({ X }), so Fluent's TS304 restriction is satisfied.
    // Decision tools are excluded from return as they have no outputs (readonly [])
    const returnProps: { [key: string]: Shape } = {}
    for (const tool of sortedTools) {
        if (!emittedToolNames.has(tool.name) || tool.type === TOOL_TYPES.DECISION) {
            continue
        }
        const varName = toolNameToVarName.get(tool.name)
        if (!varName) {
            continue
        }
        returnProps[tool.name] = new IdentifierShape({ source, name: varName })
    }

    // Use ArrowFunctionShape so the commit chain (ArrowFunctionPlugin -> VariableStatementShape
    // -> CallExpressionPlugin -> ObjectShape -> NowIncludePlugin) can patch individual properties.
    // This enables automatic Now.include() preservation for script fields, conditions,
    // and scripted inputs when users switch from inline scripts to modular files.
    return new ArrowFunctionShape({
        source,
        parameters: [paramName],
        statements,
        returnValue: new ObjectShape({ source, properties: returnProps }),
    } as any)
}
